import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {CircularProgress, Stack, Typography} from '@mui/material';
import {TAGS_SECTION_HEADING_SX} from '../tags/EntityTagsSection';
import EntityTargetsInput, {normalizeTargets, targetsToRefs} from './EntityTargetsInput';

/**
 * Shared Targets block for ICR / EEI / Gap / CT Overview.
 * Always shows the Targets heading (even when empty). Editable mode persists
 * immediately on add/remove via {@code onSave(refs)} (no separate Save button).
 *
 * @param {Object} props
 * @param {Array<string|{entityRef?:string,canonicalName?:string}>} [props.value]
 * @param {boolean} [props.canEdit=false]
 * @param {(refs: string[]) => void|Promise<void>} [props.onSave]
 * @param {boolean} [props.disabled=false]
 */
export default function RequirementTargetsSection({
    value = [],
    canEdit = false,
    onSave,
    disabled = false,
}) {
    const baseline = useMemo(() => normalizeTargets(value), [value]);
    const baselineKey = useMemo(() => targetsToRefs(baseline).join('\0'), [baseline]);

    const [localTargets, setLocalTargets] = useState(baseline);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const saveSeqRef = useRef(0);
    const localTargetsRef = useRef(localTargets);
    localTargetsRef.current = localTargets;

    useEffect(() => {
        setLocalTargets(baseline);
        setError(null);
    }, [baselineKey]); // eslint-disable-line react-hooks/exhaustive-deps -- sync on value identity

    const persist = useCallback(async (nextTargets) => {
        if (!canEdit || typeof onSave !== 'function') return;
        const seq = ++saveSeqRef.current;
        const snapshotBefore = localTargetsRef.current;
        setLocalTargets(nextTargets);
        setSaving(true);
        setError(null);
        try {
            await onSave(nextTargets.map((t) => t.entityRef));
        } catch (e) {
            if (seq !== saveSeqRef.current) return;
            setLocalTargets(snapshotBefore);
            setError(e?.message || 'Failed to save targets');
        } finally {
            if (seq === saveSeqRef.current) {
                setSaving(false);
            }
        }
    }, [canEdit, onSave]);

    const handleChange = useCallback((next) => {
        persist(normalizeTargets(next));
    }, [persist]);

    return (
        <Stack spacing={1} sx={{width: '100%', minWidth: 0}}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={TAGS_SECTION_HEADING_SX}>Targets</Typography>
                {saving && <CircularProgress size={12} aria-label="Saving targets"/>}
            </Stack>
            {canEdit ? (
                <>
                    <EntityTargetsInput
                        value={localTargets}
                        onChange={handleChange}
                        showHeading={false}
                        size="small"
                        disabled={disabled}
                    />
                    {error && (
                        <Typography color="error" variant="body2">{error}</Typography>
                    )}
                </>
            ) : baseline.length > 0 ? (
                <EntityTargetsInput value={value} readOnly showHeading={false}/>
            ) : (
                <Typography variant="body2" color="text.secondary">—</Typography>
            )}
        </Stack>
    );
}
